/**
 * Remembers the active POS draft so deep links (e.g. header basket) can restore
 * `order_id` / `serve_type_id` for `GET Store/Basket/List/`.
 * Query names must stay aligned with `commerce/pos.tsx` exports.
 */
const ORDER_PARAM = 'order_id'
const SERVE_PARAM = 'serve_type_id'

function draftStorageKey(serviceInstanceId: string) {
  return `commerce-pos-draft:${serviceInstanceId}`
}

export type CommercePosDraftPayload = {
  orderId: string | null
  serveTypeId: string | null
}

/** Write or clear draft for this branch. Clears storage when `orderId` is empty. */
export function persistCommercePosDraft(
  serviceInstanceId: string | null | undefined,
  draft: CommercePosDraftPayload,
): void {
  if (typeof window === 'undefined') {
    return
  }

  const sid = String(serviceInstanceId ?? '').trim()
  if (!sid) {
    return
  }

  const key = draftStorageKey(sid)
  const orderId = String(draft.orderId ?? '').trim()
  if (!orderId) {
    window.sessionStorage.removeItem(key)
    return
  }

  const serveTypeId = String(draft.serveTypeId ?? '').trim()
  window.sessionStorage.setItem(
    key,
    JSON.stringify({
      [ORDER_PARAM]: orderId,
      ...(serveTypeId ? { [SERVE_PARAM]: serveTypeId } : {}),
    }),
  )
}

export function readCommercePosDraft(
  serviceInstanceId: string | null | undefined,
): { orderId: string; serveTypeId: string | null } | null {
  if (typeof window === 'undefined') {
    return null
  }

  const sid = String(serviceInstanceId ?? '').trim()
  if (!sid) {
    return null
  }

  const raw = window.sessionStorage.getItem(draftStorageKey(sid))
  if (!raw) {
    return null
  }

  try {
    const parsed = JSON.parse(raw) as Record<string, unknown>
    const orderId = String(parsed[ORDER_PARAM] ?? '').trim()
    if (!orderId) {
      return null
    }
    const serveRaw = parsed[SERVE_PARAM]
    const serveTypeId =
      serveRaw != null && String(serveRaw).trim() !== '' ? String(serveRaw).trim() : null
    return { orderId, serveTypeId }
  } catch {
    return null
  }
}

/** Merge stored draft query params onto a POS href (already includes `service_instance_id` when applicable). */
export function appendCommercePosDraftToHref(href: string, serviceInstanceId?: string | null): string {
  const draft = readCommercePosDraft(serviceInstanceId)
  if (!draft) {
    return href
  }

  const [pathname, search = ''] = href.split('?')
  const params = new URLSearchParams(search)
  params.set(ORDER_PARAM, draft.orderId)
  if (draft.serveTypeId) {
    params.set(SERVE_PARAM, draft.serveTypeId)
  }

  const query = params.toString()
  return query ? `${pathname}?${query}` : pathname
}
